// Helpers shared by the admin credentials page and the agency channel popup.

// drct_name is the admin's own label; older credentials have none, so fall back
// to what identifies them on screen.
// Ids repeat across channels (one table each), so a credential is told apart by both.
export function credentialKey(credential) {
  return `${credential.channel_code}-${credential.id}`;
}

export function sameCredential(a, b) {
  return a.channel_code === b.channel_code && String(a.id) === String(b.id);
}

export function credentialPath(credential) {
  const code = encodeURIComponent(credential.channel_code);
  return `/credentials/${code}/${encodeURIComponent(credential.id)}`;
}

export function credentialDisplayName(credential, channelName) {
  if (!credential) return '';
  if (credential.drct_name) return credential.drct_name;

  const parts = [credential.channel || channelName, credential.iata_code].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return credential.id != null ? `#${credential.id}` : '';
}

export function isSecretField(field) {
  return field.encrypted === true;
}

export function isTimeZoneField(field) {
  return field.key === 'time_zone';
}

export function isBlank(value) {
  return value == null || String(value).trim() === '';
}

// Editable copies of a credential's fields. Secrets start blank because the API
// never returns them; arrays are edited as comma-separated text.
export function toFormFields(data) {
  return (Array.isArray(data) ? data : []).map((field) => ({
    ...field,
    value: toInputValue(field),
  }));
}

function toInputValue(field) {
  const { type, value } = field;
  if (isSecretField(field)) return '';
  if (type === 'Bool') return value === true || value === 'true';
  if (isTimeZoneField(field)) return isBlank(value) ? null : value;
  if (type === 'Array') return Array.isArray(value) ? value.join(', ') : (value ?? '');
  return value == null ? '' : String(value);
}

// The `data` entries sent on create/update. A blank secret means "keep the
// stored one", so a secret is only sent once the admin typed a value.
export function toPayloadData(fields) {
  return fields
    .filter((field) => !(isSecretField(field) && isBlank(field.value)))
    .map((field) => ({ key: field.key, value: toPayloadValue(field) }));
}

function toPayloadValue(field) {
  const { type, value } = field;
  if (type === 'Bool') return value === true;
  if (type === 'Array') {
    return String(value ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (type === 'Integer' || type === 'Float') {
    if (isBlank(value)) return null;
    const number = Number(String(value).trim().replace(',', '.'));
    return type === 'Integer' ? Math.trunc(number) : number;
  }
  if (isTimeZoneField(field)) return isBlank(value) ? null : value;
  return value ?? '';
}

// { [field.key]: 'required' | 'number' } for the fields that can't be sent yet.
// Secrets are required on create; a template may also flag `required: true`.
export function fieldErrors(fields, { creating }) {
  return fields.reduce((errors, field) => {
    const required = field.required === true || (creating && isSecretField(field));
    if (field.type !== 'Bool' && required && isBlank(field.value)) {
      errors[field.key] = 'required';
    } else if (!isBlank(field.value) && !validNumber(field)) {
      errors[field.key] = 'number';
    }
    return errors;
  }, {});
}

function validNumber({ type, value }) {
  const text = String(value).trim().replace(',', '.');
  if (type === 'Integer') return /^-?\d+$/.test(text);
  if (type === 'Float') return /^-?\d+(\.\d+)?$/.test(text);
  return true;
}
